import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { formatDay } from "@/lib/format";
import { Upload, Download, FileSpreadsheet, Loader2, AlertCircle } from "lucide-react";

const CANONICAL_LABELS = {
  company_name: "Company Name *",
  company_domain: "Company Domain",
  sector: "Sector",
  round_size: "Round Size",
  company_description: "Description",
  geography: "Geography",
  traction: "Traction",
  founder_first_name: "Founder First Name",
  founder_last_name: "Founder Last Name",
  founder_email: "Founder Email",
  founder_linkedin_url: "Founder LinkedIn",
  founder_role: "Founder Role",
};

export default function BulkOps() {
  const { user } = useAuth();
  const canUpload = user?.role === "vc_admin" || user?.role === "senior_vc";
  const isAdmin = user?.role === "vc_admin";
  const [tab, setTab] = useState(canUpload ? "upload" : "download");

  return (
    <div className="p-8">
      <div className="cw-caption uppercase tracking-widest">Data</div>
      <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
        <FileSpreadsheet size={22} /> Bulk Upload / Download
      </h1>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        Move deals in or out of CompeteWiser in bulk. Both operations run in the background.
      </p>

      <div className="mt-6 inline-flex rounded-md border overflow-hidden"
        style={{ borderColor: "var(--cw-border)" }}>
        {canUpload && (
          <button
            className={`text-sm px-4 py-2 ${tab === "upload" ? "font-semibold" : "text-[var(--cw-muted)]"}`}
            style={tab === "upload" ? { background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" } : {}}
            onClick={() => setTab("upload")}
            data-testid="bulk-tab-upload">
            Upload
          </button>
        )}
        <button
          className={`text-sm px-4 py-2 ${tab === "download" ? "font-semibold" : "text-[var(--cw-muted)]"}`}
          style={tab === "download" ? { background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" } : {}}
          onClick={() => setTab("download")}
          data-testid="bulk-tab-download">
          Download
        </button>
      </div>

      <div className="mt-6">
        {tab === "upload" && canUpload && <UploadPanel />}
        {tab === "download" && <DownloadPanel isAdmin={isAdmin} />}
      </div>
    </div>
  );
}

/* ---------------- UPLOAD ---------------- */
function UploadPanel() {
  const { pipelineId: ctxPipelineId, pipelines } = usePipelineCtx();
  const [step, setStep] = useState("select"); // select → map → running → done
  const [parseResp, setParseResp] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("insert_only");
  // v5.7 Addition 1: pipeline defaults from global switcher; remains changeable here.
  const [pipelineId, setPipelineId] = useState(ctxPipelineId || "");
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  // v5.8 A4: bulk mapping — expose live custom fields as destination options
  const [customFieldOptions, setCustomFieldOptions] = useState([]);
  useEffect(() => {
    if (!pipelineId) { setCustomFieldOptions([]); return; }
    api.get("/fields/live", { params: { pipeline_id: pipelineId, module: "deal" } })
      .then(({ data }) => setCustomFieldOptions(data.filter((f) => f.is_attachment && f.is_custom && f.field_type !== "computed")))
      .catch(() => setCustomFieldOptions([]));
  }, [pipelineId]);

  const loadJobs = () =>
    api.get("/bulk/jobs").then(({ data }) => setJobs(data));
  useEffect(() => { loadJobs(); }, []);

  useEffect(() => {
    if (ctxPipelineId && !pipelineId) setPipelineId(ctxPipelineId);
  }, [ctxPipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pipelineId) return;
    api.get(`/pipelines/${pipelineId}/stages`).then(({ data }) => {
      setStages(data);
      setStageId(data[0]?.stage_id || "");
    });
  }, [pipelineId]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/(\.csv|\.xlsx|\.xls)$/i.test(f.name)) {
      toast.error("Only .csv or .xlsx allowed");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File must be under 25MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/bulk/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParseResp(data);
      setMapping(data.suggested_map);
      setStep("map");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to parse file");
    } finally {
      setBusy(false);
    }
  };

  const startImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bulk/import", {
        parse_id: parseResp.parse_id,
        column_map: mapping,
        mode,
        pipeline_id: pipelineId,
        default_stage_id: stageId,
      });
      toast.success("Import queued");
      setStep("done");
      setParseResp(null);
      setMapping({});
      // Poll job status
      const poll = setInterval(async () => {
        const { data: j } = await api.get(`/bulk/jobs/${data.job_id}`);
        if (j.status === "completed" || j.status === "failed") {
          clearInterval(poll);
          loadJobs();
        } else {
          loadJobs();
        }
      }, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start import");
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = (job) => {
    const rows = ["row,reason"];
    (job.error_rows || []).forEach((r) => {
      rows.push(`${r.row},"${(r.reason || "").replace(/"/g, '""')}"`);
    });
    const csv = rows.join("\n");
    const link = document.createElement("a");
    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.download = `import_errors_${job.job_id.slice(0, 8)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {step === "select" && (
        <div className="cw-card p-8 text-center">
          <Upload size={32} className="mx-auto" style={{ color: "var(--cw-muted)" }} />
          <div className="mt-3 font-medium">Upload a CSV or XLSX file</div>
          <div className="text-xs text-[var(--cw-muted)] mt-1">Max 25MB. Only VC Admin and Senior VC can upload.</div>
          <label className="mt-4 cw-btn-primary text-sm inline-flex items-center gap-1 cursor-pointer">
            <Upload size={14} />
            {busy ? "Parsing…" : "Choose file"}
            <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={onFile}
              data-testid="bulk-file-input" />
          </label>
        </div>
      )}

      {step === "map" && parseResp && (
        <div className="space-y-4">
          <div className="cw-card p-5">
            <h3 className="cw-h3">Map columns</h3>
            <p className="text-xs text-[var(--cw-muted)] mt-1">
              {parseResp.row_count} row(s) detected. Set the destination for each column.
              At least <strong>Company Name</strong> must be mapped.
            </p>
            <div className="mt-3 space-y-2">
              {/* v5.8 Addition 7: column headers */}
              <div className="flex items-center gap-3 sticky top-0 z-10 py-1"
                style={{ background: "var(--cw-page)" }}>
                <div className="text-[10px] w-56 uppercase tracking-widest font-semibold text-[var(--cw-muted)]">
                  Your File's Columns
                </div>
                <div className="flex-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--cw-muted)]">
                  CompeteWiser Fields
                </div>
              </div>
              {parseResp.headers.map((h) => (
                <div key={h} className="flex items-center gap-3">
                  <div className="text-xs w-56 truncate" title={h}>{h}</div>
                  <select
                    value={mapping[h] || ""}
                    onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                    className="flex-1 h-8 px-2 rounded-md border text-xs"
                    style={{ borderColor: "var(--cw-border)" }}
                    data-testid={`bulk-map-${h}`}
                  >
                    <option value="">— Ignore —</option>
                    {Object.entries(CANONICAL_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                    {/* v5.8 A4: live custom-field destinations from selected pipeline */}
                    {customFieldOptions.length > 0 && (
                      <optgroup label="Custom Fields">
                        {customFieldOptions.map((f) => (
                          <option key={f.field_definition_id} value={`cf:${f.field_definition_id}`}>
                            {f.effective_label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="cw-card p-5 space-y-3">
            <h3 className="cw-h3">Import options</h3>
            <div>
              <div className="cw-caption">Mode</div>
              <label className="flex items-center gap-2 text-xs mt-1">
                <input type="radio" name="mode" value="insert_only"
                  checked={mode === "insert_only"}
                  onChange={(e) => setMode(e.target.value)}
                  data-testid="bulk-mode-insert" />
                <span>
                  <strong>Add new only</strong>
                  <span className="text-[var(--cw-muted)]"> · Skip duplicates by name/domain (report as errors)</span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-xs mt-1">
                <input type="radio" name="mode" value="upsert_by_domain"
                  checked={mode === "upsert_by_domain"}
                  onChange={(e) => setMode(e.target.value)}
                  data-testid="bulk-mode-upsert" />
                <span>
                  <strong>Match by domain & update</strong>
                  <span className="text-[var(--cw-muted)]"> · Update existing deals with matching domain, insert the rest</span>
                </span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="cw-caption">Target pipeline *</div>
                <select value={pipelineId}
                  onChange={(e) => setPipelineId(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border text-sm mt-1"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid="bulk-pipeline-select">
                  {pipelines.map((p) => (
                    <option key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="cw-caption">Default stage for new deals *</div>
                <select value={stageId}
                  onChange={(e) => setStageId(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border text-sm mt-1"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid="bulk-stage-select">
                  {stages.map((s) => (
                    <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep("select")}
              className="cw-btn-ghost text-sm">
              Back
            </button>
            <button onClick={startImport}
              disabled={busy || !Object.values(mapping).includes("company_name")}
              className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="bulk-start-import">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Queuing…" : "Start Import"}
            </button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div className="cw-card p-5">
          <div className="text-sm">
            Import queued. Check <strong>Bulk Uploads</strong> history below.
          </div>
          <button onClick={() => setStep("select")}
            className="cw-btn-primary text-sm mt-3">
            Upload another file
          </button>
        </div>
      )}

      {/* History */}
      <div className="cw-card p-5">
        <h3 className="cw-h3">Bulk Uploads</h3>
        <div className="mt-3 space-y-2" data-testid="bulk-jobs-list">
          {jobs.length === 0 && (
            <div className="text-xs text-[var(--cw-muted)] italic">
              No imports yet.
            </div>
          )}
          {jobs.map((j) => (
            <div key={j.job_id} className="border rounded-md p-3"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid={`bulk-job-${j.job_id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">{j.filename}</div>
                  <div className="text-xs text-[var(--cw-muted)] mt-0.5">
                    {formatDay(j.created_at)} · Mode: {j.mode === "insert_only" ? "Add new" : "Match by domain"}
                  </div>
                  {j.status === "completed" ? (
                    <div className="text-xs mt-2">
                      <span className="text-green-700 font-medium">{j.rows_created || 0} created</span>
                      {" · "}
                      <span className="text-blue-700 font-medium">{j.rows_updated || 0} updated</span>
                      {" · "}
                      <span className="text-orange-700 font-medium">{j.rows_skipped || 0} skipped</span>
                      {(j.error_rows?.length || 0) > 0 && (
                        <button onClick={() => downloadErrors(j)}
                          className="ml-3 underline text-[var(--cw-primary)]"
                          data-testid={`bulk-download-errors-${j.job_id}`}>
                          Download error report ({j.error_rows.length})
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs mt-1 inline-flex items-center gap-1">
                      {j.status === "processing" && <Loader2 size={12} className="animate-spin" />}
                      Status: {j.status}
                      {j.error && <span className="text-[var(--cw-error)] ml-2">{j.error}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- DOWNLOAD ---------------- */
function DownloadPanel({ isAdmin }) {
  const { pipelineId: ctxPipelineId, pipelines } = usePipelineCtx();
  // v5.7 Addition 20: tenant-wide export scope removed. Only pipeline scope remains.
  const [pipelineId, setPipelineId] = useState(ctxPipelineId || "");
  const [format, setFormat] = useState("xlsx");
  const [includeFounders, setIncludeFounders] = useState(false);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (ctxPipelineId && !pipelineId) setPipelineId(ctxPipelineId);
  }, [ctxPipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (!pipelineId) { toast.error("Please select a pipeline"); return; }
    setBusy(true);
    setJob(null);
    try {
      const { data } = await api.post("/bulk/export", {
        pipeline_id: pipelineId,
        format,
        include_founders_sheet: format === "xlsx" ? includeFounders : false,
      });
      toast.success("Export queued");
      const poll = setInterval(async () => {
        const { data: j } = await api.get(`/bulk/exports/${data.job_id}`);
        setJob(j);
        if (j.status === "completed" || j.status === "failed") {
          clearInterval(poll);
        }
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start export");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!job?.data_base64) return;
    const link = document.createElement("a");
    link.href = `data:${job.content_type};base64,${job.data_base64}`;
    link.download = job.filename;
    link.click();
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="cw-card p-5 space-y-3">
        <div>
          <div className="cw-caption">Pipeline</div>
          <select value={pipelineId}
            onChange={(e) => setPipelineId(e.target.value)}
            className="w-full h-9 px-3 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="export-pipeline-select">
            {pipelines.map((p) => (
              <option key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="cw-caption">Format</div>
          <label className="flex items-center gap-2 text-xs mt-1">
            <input type="radio" name="format" value="xlsx"
              checked={format === "xlsx"}
              onChange={(e) => setFormat(e.target.value)}
              data-testid="export-format-xlsx" />
            <span>Excel (.xlsx)</span>
          </label>
          <label className="flex items-center gap-2 text-xs mt-1">
            <input type="radio" name="format" value="csv"
              checked={format === "csv"}
              onChange={(e) => setFormat(e.target.value)}
              data-testid="export-format-csv" />
            <span>CSV</span>
          </label>
        </div>
        {format === "xlsx" && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox"
              checked={includeFounders}
              onChange={(e) => setIncludeFounders(e.target.checked)}
              data-testid="export-include-founders" />
            Include all founders on a second sheet
          </label>
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={start} disabled={busy}
          className="cw-btn-primary text-sm inline-flex items-center gap-1"
          data-testid="export-start">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {busy ? "Queuing…" : "Generate export"}
        </button>
      </div>

      {job && (
        <div className="cw-card p-5"
          data-testid="export-status">
          <div className="text-sm">
            Status: <strong>{job.status}</strong>
          </div>
          {job.status === "completed" && (
            <div className="mt-2 space-y-2">
              <div className="text-xs text-[var(--cw-muted)]">
                {job.rows_total} row(s) · {job.filename}
              </div>
              <button onClick={download}
                className="cw-btn-primary text-sm inline-flex items-center gap-1"
                data-testid="export-download">
                <Download size={14} /> Download
              </button>
            </div>
          )}
          {job.status === "failed" && (
            <div className="mt-2 text-xs text-[var(--cw-error)] inline-flex items-center gap-1">
              <AlertCircle size={12} /> {job.error || "Unknown error"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
